import React, {useEffect, useState, useId} from 'react';
import styles from './modal.module.scss';
import { createPortal } from 'react-dom';
import CloseIcon from '../../assets/icons/close.svg?react';
import { useModalContext } from '../modal-context/ModalContext';
import TranslationBadge from '../translation-provider/TranslationBadge';
import { useUser } from '@src/hooks/useUser';
import { logger } from '../../utils/logger';

type Props = {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  global?: boolean;
  noPadding?: boolean;
  closeButton?: boolean;
  className?: string;
  closeOnOverlayClick?: boolean;
  id?: string;
};

const platform = window.navigator.platform;

function Modal(props: Props) {
  const { open, onClose, children, global = true, closeButton = true, className = '', noPadding, closeOnOverlayClick = true, id } = props;
  const [isWindows, setIswindows] = useState(false);
  const generatedId = useId();
  const modalId = id || generatedId;
  const { registerModal, unregisterModal } = useModalContext();
  const [user] = useUser();
  
  // Check if user is from Australia
  const isAUuser = user?.id?.toLowerCase().startsWith("au");

  useEffect(() => {
    if (['Win3', 'Win64', 'Windows', 'Win32', 'Win'].indexOf(platform) !== -1) {
      setIswindows(true);
    }
  }, []);

  // Register/unregister modal with context when opened/closed
  useEffect(() => {
    if (open) {
      logger.info('[Modal] Registering modal with ID:', modalId);
      registerModal(modalId);
    } else {
      logger.info('[Modal] Unregistering modal with ID:', modalId);
      unregisterModal(modalId);
    }
    
    // Clean up on unmount
    return () => {
      logger.info('[Modal] Unmounting and unregistering modal with ID:', modalId);
      unregisterModal(modalId);
    };
  }, [modalId, open, registerModal, unregisterModal]);

  const renderContent = () => {
    return (
      <div className={`${styles.inner} ${className} ${isWindows && styles.innerWindows} ${noPadding && styles.innerOverride}`}>
          {closeButton && onClose &&
          <button onClick={onClose} className={styles.closeWrapper}>
            <CloseIcon className={styles.closeIcon}/>
          </button>
          }
          {/* Add translation badge to modal - hidden for Australian users */}
          {!isAUuser && <TranslationBadge type="modal" />}
          {children}
      </div>
    )
  }

  if (!open) return null;

  if (global) {
    return createPortal(
      <div className={styles.global}>
        <div className={styles.backdrop} onClick={closeOnOverlayClick ? onClose : () => {}} />
        {renderContent()}
      </div>,
      document.body
    );
  }

  return (
    <div className={styles.local}>
      <div className={styles.backdrop} onClick={closeOnOverlayClick ? onClose : () => {}} />
      {renderContent()}
    </div>
  )
}

export default Modal;
